<template>
    <div class="h-full w-full">
        <div class="mx-auto container p-4">
            <Loading :loading="loading" class="min-h-screen">
                <!-- Toolbar -->
                <div v-if="!loading" class="flex flex-wrap gap-4 justify-end items-center">
                    <!-- Search -->
                    <div class="max-w-[150px] grow focus-within:max-w-[300px] transition-all ease-out duration-700">
                        <Input
                            type="search"
                            name="Suchen"
                            :formComponentId="'contentstream_search'"
                            :label="$t('Searching...')"
                            v-model:value="contentstream_search"
                            @keyup.enter="search"
                            @search="search"
                            autocomplete="Suchen..."
                            class="w-full rounded-lg text-xs m-1"
                        />
                    </div>

                    <!-- filter -->
                    <div v-click-outside="() => (filterOpened = false)">
                        <div
                            @click="filterOpened = !filterOpened"
                            class="flex flex-row gap-2 cursor-pointer p-2"
                            :class="selected_product_ids.length > 0 || published_at ? 'bg-blue rounded-md text-white' : ''"
                        >
                            <Icon type="Filter" class="fill-current" viewBox="0 0 21 20" width="21" height="20" />
                            <p>{{ $t('plain_filter') }}</p>
                        </div>
                        <TransitionExpand v-show="filterOpened" class="absolute top-14 right-0 z-[60] w-full lg:w-[900px]">
                            <div class="rounded-md shadow-md p-4 bg-white">
                                <div class="flex flex-col lg:flex-row gap-4">
                                    <div class="w-full lg:h-[286px] overflow-y-scroll" v-if="Object.keys(products).length > 1">
                                        <p class="mb-4">{{ $t('Filter Markets') }}</p>
                                        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-x-4">
                                            <div v-for="(name, id) in products" :key="id">
                                                <Checkbox
                                                    :name="$translate(name)"
                                                    :label="$translate(name)"
                                                    @update:value="selectProductID(id)"
                                                    :value="selected_product_ids.includes(id)"
                                                    class="my-1"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div class="w-full lg:h-[286px] select-none" v-if="Object.keys(stockTags).length > 0">
                                        <div class="flex items-center gap-2 mb-2">
                                            <div class="w-5 opacity-40">
                                                <svg xmlns="http://www.w3.org/2000/svg" class="ionicon" viewBox="0 0 512 512">
                                                    <path
                                                        d="M435.25 48h-122.9a14.46 14.46 0 00-10.2 4.2L56.45 297.9a28.85 28.85 0 000 40.7l117 117a28.85 28.85 0 0040.7 0L459.75 210a14.46 14.46 0 004.2-10.2v-123a28.66 28.66 0 00-28.7-28.8z"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        stroke-linecap="round"
                                                        stroke-linejoin="round"
                                                        stroke-width="32"
                                                    />
                                                    <path d="M384 160a32 32 0 1132-32 32 32 0 01-32 32z" />
                                                </svg>
                                            </div>
                                            <div>{{ $t('Filter Stocks') }}</div>
                                        </div>
                                        <div class="flex items-center gap-2">
                                            <input
                                                class="rounded-md w-full mb-2 px-2 border border-gray-300 focus:border-blue focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                type="text"
                                                v-model="tagFilterValue"
                                                @keyup="quickFilter"
                                                placeholder="Filter..."
                                            />
                                            <div v-if="this.selected_stock_tags.length > 0" class="flex justify-between items-center mb-2 text-xs">
                                                <div class="bg-blue/5 text-blue-dark rounded-full flex items-center gap-2">
                                                    <span class="pl-3">{{ this.selected_stock_tags.length }}</span>
                                                    <span class="py-1">{{ $t('selected') }}</span>
                                                    <span @click="clearTagSelection" class="text-white bg-blue rounded-full w-5 h-5 select-none cursor-pointer text-center">X</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="lg:h-[222px] overflow-y-scroll">
                                            <div class="flex flex-wrap gap-2">
                                                <div v-for="tag in stockTagsRender">
                                                    <CheckboxAlt
                                                        :name="tag"
                                                        :label="tag"
                                                        @update:value="toggleStockTag(tag)"
                                                        :value="selected_stock_tags.includes(tag)"
                                                        class="my-1"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <Datepicker
                                        :modelValue="published_at"
                                        @update:modelValue="selectPublishedAt"
                                        :model-type="calendarModeltype"
                                        class="!m-0"
                                        :class="products ? 'w-full z-50 relative' : 'w-1/2 lg:w-full z-50 relative'"
                                        :locale="calendarLocale"
                                        :selectText="$t('plain_select')"
                                        :enableTimePicker="false"
                                        :range="true"
                                        inline
                                        autoApply
                                        :monday-first="true"
                                    />
                                </div>
                                <hr class="border-gray-500 w-full mt-2 mb-4" />
                                <div class="flex flex-row gap-2 justify-between">
                                    <p @click="reset()" class="text-blue cursor-pointer">{{ $t('plain_reset') }}</p>
                                    <p @click="search()" class="text-blue cursor-pointer">{{ $t('plain_apply') }}</p>
                                </div>
                            </div>
                        </TransitionExpand>
                    </div>

                    <!-- more options -->
                    <div v-if="this.user?.email.endsWith('@hkcmanagement.de')" v-click-outside="() => (moreOptionOpened = false)" class="relative">
                        <div
                            @click="moreOptionOpened = !moreOptionOpened"
                            class="cursor-pointer p-2 rounded-md hover:bg-blue/5 hover:shadow-md"
                            :class="moreOptionOpened ? 'bg-blue/5 shadow-md' : ''"
                        >
                            <!-- <Icon type="List" viewBox="0 0 21 17" width="21" height="17" class="shrink-0"/> -->
                            <Icon :type="moreOptionOpened ? 'Notify' : 'NotifyBlack'" viewBox="0 0 76 76" />
                        </div>
                        <div v-show="moreOptionOpened" class="arrow-up absolute right-2 z-20"></div>
                        <TransitionExpand v-show="moreOptionOpened" class="absolute right-0 top-11 w-48 z-10">
                            <div class="bg-white shadow-md rounded-md p-1 space-y-1">
                                <div class="hover:bg-blue/5 p-1 px-2 rounded-md cursor-pointer text-xs" @click="markAllAsRead">{{ $t('Mark all as read') }}</div>
                                <div class="hover:bg-blue/5 p-1 px-2 rounded-md cursor-pointer text-xs" @click="markAllAsUnread">{{ $t('Mark all as unread') }}</div>
                            </div>
                        </TransitionExpand>
                    </div>
                </div>

                <!-- tab contentstreams type -->
                <Tabs
                    v-if="!loading"
                    v-model:data="tabs"
                    activeColor="#000000"
                    v-model:activeTab="activeTab"
                    :overlay="false"
                    align="start"
                    :firstOnLoad="false"
                    class="pt-4"
                    @update:activeTab="changeTab"
                >
                    <template #header="{ data }">
                        <strong>
                            {{ $translate(data.title) }}
                        </strong>
                    </template>
                    <!-- single contentstreams block -->
                    <template #item="{ data }">
                        <Loading :loading="innerLoading" class="w-full min-h-12">
                            <div class="grid grid-cols-1 lg:grid-cols-3 gap-4" v-if="!innerLoading">
                                <SingleContentstream
                                    v-for="contentstream in $store.getters['contentstreams/getByKey'](data.id).data"
                                    :key="contentstream.id"
                                    :data="contentstream"
                                    :page="page"
                                    :active_tab="active_tab"
                                    :query_params="query_params?.toString()"
                                />
                            </div>
                            <p v-if="$store.getters['contentstreams/getByKey'](data.id)?.data?.length <= 0">
                                {{ $t('No Data') }}
                            </p>
                            <!-- Pager -->
                            <div v-if="!innerLoading" class="flex justify-end mt-10">
                                <Pagination :limit="3" :data="$store.getters['contentstreams/getByKey'](data.id)" @pagination-change-page="selectTab" />
                            </div>
                        </Loading>
                    </template>
                </Tabs>
            </Loading>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import api from '../../api';
import _ from 'lodash';

export default {
    mounted() {
        this.loading = true;
        this.getAvailableProducts();

        // initially set data from query
        this.active_tab = this.$route?.query?.active_tab || 'all_analysis';
        this.published_at = this.$route?.query?.published_at?.split(',') || null;
        this.selected_product_ids = this.$route?.query?.selected_product_ids?.split(',') || [];
        this.selected_stock_tags = this.$route?.query?.selected_stock_tags?.split(',') || [];
        this.contentstream_search = this.$route?.query?.search_phrase || null;
        this.page = this.$route?.query?.page || 1;

        this.$store.dispatch('contentstream_types/index').then((contentstream_types) => {
            this.special = contentstream_types.data.find((contentstream_type) => contentstream_type.name === 'special');
            this.short = contentstream_types.data.find((contentstream_type) => contentstream_type.name === 'short');
            this.dmu = contentstream_types.data.find((contentstream_type) => contentstream_type.name === 'dmu');
            this.dsu = contentstream_types.data.find((contentstream_type) => contentstream_type.name === 'dsu');
            this.wmu = contentstream_types.data.find((contentstream_type) => contentstream_type.name === 'wmu');
            this.webinar = contentstream_types.data.find((contentstream_type) => contentstream_type.name === 'webinar');
            this.activeTab = this.tabs.find((tab) => tab.id === this.active_tab);
            this.loading = false;

            this.$nextTick(() => {
                this.selectTab(this.page);
            });
        });
    },

    methods: {
        // reset filter
        reset() {
            this.filterOpened = false;
            this.moreOptionOpened = false;
            this.published_at = null;
            this.contentstream_search = null;
            this.selected_product_ids = [];
            this.selected_stock_tags = [];
            this.replaceQueryParams({ contentstream_search: '' });
            this.replaceQueryParams({ published_at: '' });
            this.replaceQueryParams({ selected_product_ids: '' });
            this.replaceQueryParams({ contentstream_stocks: '' });
            this.indexContentstreams();
        },

        async indexContentstreams(params) {
            this.innerLoading = true;

            let published_at_formatted = null;

            if (this.published_at) {
                if (this.published_at[0] && this.published_at[1]) {
                    published_at_formatted = [];
                    published_at_formatted[0] = new Date(this.$moment(this.published_at[0]).startOf('day'));
                    published_at_formatted[1] = new Date(this.$moment(this.published_at[1]).endOf('day'));
                } else if (this.published_at[0]) {
                    published_at_formatted = [];
                    published_at_formatted[0] = new Date(this.$moment(this.published_at[0]).startOf('day'));
                    published_at_formatted[1] = new Date(this.$moment(this.published_at[0]).endOf('day'));
                }
            }

            let config = {
                params: {
                    'sort[by]': 'published_at',
                    'sort[order]': 'desc',
                    key: this.active_tab || 'all_analysis',
                    active_tab: this.active_tab || 'all_analysis',
                    page: this.page || 1,
                    search_phrase: this.contentstream_search,
                    product_id: this.selected_product_ids || null,
                    published_at: published_at_formatted,
                    contentstream_type_id: this.contentstream_type_id,
                    contentstream_stocks: this.selected_stock_tags
                }
            };

            _.merge(config.params, params);

            return await this.$store.dispatch('contentstreams/index', config).then(() => {
                this.innerLoading = false;
            });
        },

        // Available products for customer
        async getAvailableProducts() {
            await api.get('/api/v1/contentstreams/filters').then((response) => {
                this.products = {};
                let productsRaw = response.data.products_without_webinars;
                let sortedIds = Object.keys(productsRaw).sort((a, b) => productsRaw[a].localeCompare(productsRaw[b]));

                sortedIds.forEach((element) => {
                    this.products[element] = productsRaw[element];
                });

                // NOTE: For Stock Tags
                this.stockTags = Object.values(response.data.contentstream_stocks).sort();
                this.stockTagsRender = Object.values(response.data.contentstream_stocks).sort();

                // TODO: Für Videos
                // this.products = response.data.products_only_webinars
            });
        },

        // filter search (anwenden)
        search() {
            this.filterOpened = false;
            this.moreOptionOpened = false;
            this.page = 1;
            this.replaceQueryParams({ contentstream_search: this.contentstream_search, page: 1 });
            this.indexContentstreams();
        },

        markAllAsRead() {
            console.log('mark all as read');
            this.innerLoading = true;
            this.moreOptionOpened = false;

            this.$store.dispatch('contentstream_user/markAllAsRead', {
                userId: this.user.id
            });

            setTimeout(() => {
                this.indexContentstreams();
            }, 650);
        },

        markAllAsUnread() {
            console.log('mark all as unread');
            this.moreOptionOpened = false;
        },

        // filter products
        selectProductID(product_id) {
            if (this.selected_product_ids.includes(product_id)) {
                this.selected_product_ids.splice(this.selected_product_ids.indexOf(product_id), 1);
                this.replaceQueryParams({ selected_product_ids: this.selected_product_ids, page: 1 });
            } else {
                this.selected_product_ids.push(product_id);
                this.replaceQueryParams({ selected_product_ids: this.selected_product_ids, page: 1 });
            }

            // update query accordingly
            if (this.selected_product_ids.length <= 0) {
                this.replaceQueryParams({ selected_product_ids: '' });
            }

            this.indexContentstreams();
        },

        // NOTE: Filter Stocks tags
        toggleStockTag(tag_mame) {
            // NOTE: If tag is already selected, remove it
            if (this.selected_stock_tags.includes(tag_mame)) {
                this.selected_stock_tags.splice(this.selected_stock_tags.indexOf(tag_mame), 1);

                // NOTE: If tag is not selected, add it
            } else {
                this.selected_stock_tags.push(tag_mame);
            }

            this.replaceQueryParams({ contentstream_stocks: this.selected_stock_tags, page: 1 });

            // update query accordingly
            if (this.selected_stock_tags.length <= 0) {
                this.replaceQueryParams({ contentstream_stocks: '' });
            }

            this.indexContentstreams();
        },

        // filter set date
        selectPublishedAt(published_at) {
            if (published_at) {
                this.published_at = [published_at[0], published_at[1]];
            } else {
                this.published_at = null;
            }

            // update query accordingly
            if (this.published_at) {
                this.replaceQueryParams({
                    published_at: this.published_at,
                    page: 1
                });

                this.page = 1;
            } else {
                this.replaceQueryParams({ published_at: '' });
            }

            this.indexContentstreams();
        },

        // contentstream type Tab
        selectTab(page = 1) {
            this.page = page;

            if (this.activeTab?.id) {
                if (this.activeTab.id === 'all_analysis') {
                    this.contentstream_type_id = [this.dmu.id, this.dsu.id, this.wmu.id];
                } else if (this.activeTab.id === 'short') {
                    this.contentstream_type_id = [this.short.id];
                } else if (this.activeTab.id === 'special') {
                    this.contentstream_type_id = [this.special.id];
                }

                this.active_tab = this.activeTab.id;
            }

            this.replaceQueryParams({
                page: this.page,
                active_tab: this.activeTab?.id
            });

            this.indexContentstreams();
        },

        // reset page to first page if tab is going to change
        changeTab() {
            this.selectTab(1);
        },

        quickFilter() {
            console.log('quick filter', this.tagFilterValue);
            this.stockTagsRender = this.stockTags.filter((tag) => tag.toLowerCase().includes(this.tagFilterValue));
        },

        clearTagSelection() {
            this.selected_stock_tags = [];
            // this.replaceQueryParams({contentstream_stocks: this.selected_stock_tags, page: 1})
            this.indexContentstreams();
        }
    },

    computed: {
        query_params() {
            let params = {};

            this.active_tab ? (params['active_tab'] = this.active_tab) : (params['active_tab'] = 'all_analysis');

            this.published_at ? (params['published_at'] = this.published_at) : '';

            this.selected_product_ids?.length > 0 ? (params['selected_product_ids'] = this.selected_product_ids) : '';

            this.contentstream_search?.length > 0 ? (params['search_phrase'] = this.contentstream_search) : '';

            this.page ? (params['page'] = this.page) : (params['page'] = 1);

            params['type'] = 'contentstreams';

            let combinedParams = new URLSearchParams(params);

            return combinedParams;
        },
        region() {
            return window.meta.region_configuration.region || 'de';
        },
        calendarLocale() {
            return this.region === 'de' ? 'de-DE' : 'en-GB';
        },
        calendarModeltype() {
            return this.region === 'de' ? 'dd.mm.yyyy' : 'yyyy-mm-dd';
        },
        ...mapGetters('user', {
            user: 'get'
        }),
        ...mapGetters('contentstreams', {
            getByKey: 'getByKey'
        })
    },

    data: () => ({
        tabs: [
            {
                title: 'Meine Analysen',
                id: 'all_analysis',
                items: []
            },
            {
                title: 'Kurznachrichten',
                id: 'short',
                items: []
            },
            {
                title: 'Sondernachrichten',
                id: 'special',
                items: []
            }
            // {
            //     title: "Videoanalysen",
            //     id: "webinar",
            //     items: []
            // }
        ],
        activeTab: null,
        contentstream_search: null,
        short: null,
        dmu: null,
        wmu: null,
        special: null,
        webinar: null,
        loading: true,
        innerLoading: false,
        filterOpened: false,
        moreOptionOpened: false,
        published_at: null,
        selected_product_ids: [],
        selected_stock_tags: [],
        products: [],
        stockTags: [],
        stockTagsRender: [],
        contentstream_type_id: null,
        tagFilterValue: '',
        page: 1,
        active_tab: null
    })
};
</script>

<style scoped>
.arrow-up {
    width: 0;
    height: 0;
    border-left: 10px solid transparent;
    border-right: 10px solid transparent;

    border-bottom: 10px solid white;
}
</style>
